import i18n from '@i18next/i18n';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { ISyncFileManager, ISyncFileToast } from '@src/slide/application/service/sync-file/core/sync-file-manager.interface';
import { SYNC_TYPE, TOAST_OCCASION } from '@src/slide/application/service/sync-file/core/constants';
import { showGuideInterFlow, hideGuideInterFlow } from '@src/js/common/ark-extensions/dc-components/guide-interflow';
import { IS_SYNC_LIST, TOAST_FIRST_SHOW, TOAST_NOT_FIRST_SHOW, TOAST_SUCCESS } from './diff-const';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

export default class SyncFileToast implements ISyncFileToast {
    private syncFileManager: ISyncFileManager;
    private toastOccasion: TOAST_OCCASION;
    private callback: any;

    constructor(manager: ISyncFileManager) {
        this.syncFileManager = manager;
    }

    /**
     * 展示同步文档引导
     * 1. 首次访问 localStorage 中没有标识，并且开关返回值为"不同步"，弹出引导
     * 2. 生成副本时，开关返回值为"不同步"时弹出引导，引导后重新设置同步类型则继续生成副本，如果同步类型仍为"不同步"，则不生成副本
     * @param toastOccasion
     * @param callback
     */
    public showSyncToast = (toastOccasion: TOAST_OCCASION, callback?) => {
        this.toastOccasion = toastOccasion;
        this.callback = callback;
        const { currentSyncType } = this.syncFileManager;
        if (toastOccasion === TOAST_OCCASION.FIRST_SHOW) {
            const isSync = userLocalStorage.getItem(LocalStorageKeys.IS_SYNC_LIST);
            if (isSync) {
                return;
            }
            userLocalStorage.setItem(LocalStorageKeys.IS_SYNC_LIST, currentSyncType);
        }

        if (currentSyncType !== SYNC_TYPE.NOT) {
            callback?.();
            return;
        }

        this.showToast();
    };

    private showToast() {
        if (browser.isMobile) {
            showGuideInterFlow({
                handleClose: this.handleGuideInterFlowClose,
                isCopy: this.toastOccasion === TOAST_OCCASION.GENERATE_COPIES,
            });
        } else {
            const message = this.toastOccasion === TOAST_OCCASION.FIRST_SHOW
                ? TOAST_FIRST_SHOW
                : TOAST_NOT_FIRST_SHOW;
            const config = {
                message,
                action: [{
                    text: i18n.t('暂不'),
                    onClick: this.handleCloseWithGenerateCopies,
                }, {
                    text: i18n.t('添加'),
                    onClick: this.handleCurrentFileClick,
                }],
                onClose: this.handleClose,
                autoClose: false,
            };
            this.showSnackbar(config);
        }
    }

    private handleGuideInterFlowClose = (toggleType) => {
        hideGuideInterFlow();
        this.syncFileManager.currentSyncType = toggleType;
        this.handleClose();
    };

    private handleCurrentFileClick = () => {
        this.syncFileManager.updateSyncFileType(SYNC_TYPE.CURRENT)
            .then(() => {
                const { currentSyncType } = this.syncFileManager;
                if (currentSyncType === SYNC_TYPE.CURRENT) {
                    const message = this.toastOccasion === TOAST_OCCASION.FIRST_SHOW
                        ? TOAST_SUCCESS
                        : i18n.t('已设置，以后都默认执行该操作。');
                    const config = {
                        message,
                        action: [{
                            text: i18n.t('暂不'),
                        }, {
                            text: i18n.t('确定'),
                            onClick: this.handleAllFileClick,
                        }],
                        type: 'success',
                        onClose: this.handleClose,
                        autoClose: false,
                    };
                    this.showSnackbar(config);
                }
            });
    };

    private handleAllFileClick = () => {
        this.syncFileManager.updateSyncFileType(SYNC_TYPE.ALL);
    };

    private handleCloseWithGenerateCopies = () => {
        if (this.toastOccasion === TOAST_OCCASION.GENERATE_COPIES) {
            const config = {
                message: i18n.t('生成副本失败'),
                type: 'error',
            };
            this.showSnackbar(config);
        }
    };

    private handleClose = () => {
        const { currentSyncType } = this.syncFileManager;
        if (currentSyncType !== SYNC_TYPE.NOT) {
            this.callback?.();
        }
    };

    private showSnackbar(config) {
        if (!browser.isMobile) {
            import('@tencent/dui/lib/components/Snackbar').then((module) => {
                const Snackbar = module.default;
                Snackbar.show(config);
            });
        }
    }
}
